import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./index.scss";

import Icon from "../../components/appShell/icons";
import Api from "../../store/networking/general/ApiCalls";

// The front door. It used to be a full-bleed photograph with two blue buttons
// dropped near the bottom edge; it is now the same panel the rest of the app
// uses, asking the one question it exists to ask.
//
// Two of the four ways in are public: the live board and the pool are keyed on
// the session code alone, with nothing to sign in to. So those two rows ask for
// the code in place rather than navigating to a page that can only turn round
// and say it needed one - which is what the pool used to do, since /playerlist
// with no session renders a dead end telling you to go and find a link.
const ROLES = [
  {
    to: "/curator/auction",
    icon: "pool",
    title: "Curator",
    blurb: "Run the room. Start a category, take bids, and sell.",
  },
  {
    to: "/bidder/bidding",
    icon: "squad",
    title: "Bidder",
    blurb: "Bid for your franchise and build your squad.",
  },
  {
    key: "live",
    path: (code) => `/live/${code}`,
    icon: "live",
    title: "Live board",
    blurb: "Watch the auction as it happens.",
  },
  {
    key: "pool",
    path: (code) => `/playerlist/${code}`,
    icon: "list",
    title: "Follow along",
    blurb: "The full auction pool and what each player went for.",
  },
];

// Codes are issued as three groups of three, so the field keeps them in that
// shape however they are typed or pasted. The hyphens are punctuation rather
// than content: they go in as the groups fill and come out again on a
// backspace, so nobody can get stuck on a separator they did not type.
const CODE_LENGTH = 9;

export function formatCode(raw) {
  const clean = String(raw || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, CODE_LENGTH);

  return clean.match(/.{1,3}/g)?.join("-") ?? "";
}

export const isCompleteCode = (code) =>
  /^[a-z0-9]{3}-[a-z0-9]{3}-[a-z0-9]{3}$/.test(code);

// Asks the session for its block status purely to find out whether it exists.
// Checking here costs one request and saves sending someone to a board that
// would sit on its loading state forever, which is what a wrong code looks like
// from the other side.
async function sessionExists(code) {
  const res = await Api.blockStatus(code);
  return res?.statusCode === 200;
}

function CodeForm({ role, onCancel }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  // A component that unmounts while its request is in flight must not try to
  // set state afterwards, and an auction that has just been reset is exactly
  // when someone closes the row mid-check.
  const alive = useRef(true);

  useEffect(() => {
    inputRef.current?.focus();
    return () => {
      alive.current = false;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (checking) return;

    if (!isCompleteCode(code)) {
      setError("A code looks like abc-def-ghi.");
      return;
    }

    setChecking(true);
    setError("");

    try {
      if (await sessionExists(code)) {
        navigate(role.path(code));
        return;
      }
      if (alive.current) setError("No auction is running under that code.");
    } catch {
      // The code itself may well be right, so this says what actually failed
      // rather than blaming what was typed.
      if (alive.current) setError("Could not reach the auction. Try again.");
    }

    if (alive.current) setChecking(false);
  };

  return (
    <form className="landing-code" onSubmit={submit}>
      <label className="landing-code-label" htmlFor={`code-${role.key}`}>
        Session code
      </label>

      <div className="landing-code-row">
        <input
          id={`code-${role.key}`}
          ref={inputRef}
          className="landing-code-input"
          value={code}
          onChange={(e) => {
            setCode(formatCode(e.target.value));
            setError("");
          }}
          onKeyDown={(e) => e.key === "Escape" && onCancel()}
          placeholder="abc-def-ghi"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="text"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `code-${role.key}-error` : undefined}
        />

        <button
          type="submit"
          className="landing-code-go"
          disabled={checking || !isCompleteCode(code)}
        >
          {checking ? "Checking" : "Open"}
        </button>
      </div>

      {error ? (
        <p className="landing-code-error" id={`code-${role.key}-error`} role="alert">
          {error}
        </p>
      ) : (
        <p className="landing-code-hint">
          The organiser shares this when the auction starts.
        </p>
      )}
    </form>
  );
}

export default function Landing() {
  // Only one row is ever open: two code fields side by side would leave it
  // ambiguous which one a typed code was going to.
  const [openKey, setOpenKey] = useState(null);

  return (
    <div className="landing">
      <main className="landing-panel">
        <div className="landing-mark">A</div>
        <h1 className="au-heading">Auction</h1>
        <p className="au-subheading landing-sub">
          Choose how you are joining today.
        </p>

        <div className="landing-roles">
          {ROLES.map((role) => {
            const body = (
              <>
                <span className="landing-role-icon">
                  <Icon name={role.icon} size={22} />
                </span>
                <span className="landing-role-text">
                  <span className="landing-role-title">{role.title}</span>
                  <span className="landing-role-blurb">{role.blurb}</span>
                </span>
              </>
            );

            if (role.to) {
              return (
                <Link key={role.to} to={role.to} className="landing-role">
                  {body}
                </Link>
              );
            }

            const open = openKey === role.key;

            return (
              <div
                key={role.key}
                className={`landing-role-group${open ? " is-open" : ""}`}
              >
                <button
                  type="button"
                  className="landing-role"
                  aria-expanded={open}
                  onClick={() => setOpenKey(open ? null : role.key)}
                >
                  {body}
                  <span className="landing-role-chevron" aria-hidden="true">
                    <Icon name="back" size={18} />
                  </span>
                </button>

                {open ? (
                  <CodeForm role={role} onCancel={() => setOpenKey(null)} />
                ) : null}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
